import { useMemo } from "react"

import { orderMetricIds, type MetricViews } from "@/types/MetricViewConfig"
import type { RunRowLike } from "@/types/RunRow"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import type { RunResultsMetricRenderAdaptersById } from "../types"
import type { RunResultsTableColumn } from "./useRunResultsTableColumns"

export const COMPARE_BY_COLUMN_KEY = "compareBy"

export interface RunResultsExplanationTableColumn {
  key: string
  title: string
  width?: string
  minWidth?: string
  maxWidth?: string
}

export interface RunResultsExplanationTable {
  columns: RunResultsExplanationTableColumn[]
  getRowValues: (row: RunRowLike) => Record<string, string>
}

interface UseRunResultsColumnDefsParams {
  compareColumnLabel: string | null
  explanationTable: RunResultsExplanationTable
  getMetricDisplayLabel: (metricKey: string, isCompact: boolean) => string
  isCompactColumns: boolean
  metricIdFilter: string | null
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  metricViews?: MetricViews
  runRows: RunRowLike[]
  showMutationColumn: boolean
}

export function useRunResultsColumnDefs({
  compareColumnLabel,
  explanationTable,
  getMetricDisplayLabel,
  isCompactColumns,
  metricIdFilter,
  metricRenderAdaptersById,
  metricViews,
  runRows,
  showMutationColumn,
}: UseRunResultsColumnDefsParams): RunResultsTableColumn[] {
  return useMemo(() => {
    const inputColumn: RunResultsTableColumn = {
      key: "inputStr",
      label: "Test Case",
      width: "80px",
      maxWidth: "min(20%, 200px)",
    }

    const baseColumns: RunResultsTableColumn[] = [
      { key: "index", label: "#", width: "20px" },
      { key: "errorStatus", label: "", fullLabel: "Status", width: "20px" },
      inputColumn,
      {
        key: "testCaseDescription",
        label: "Description",
        width: "240px",
        minWidth: "200px",
      },
      { key: "simulationOutputId", label: "ID", width: "" },
      ...(showMutationColumn
        ? [{ key: "mutationName", label: "Mutation", width: "" }]
        : []),
      ...(compareColumnLabel
        ? [
            {
              key: COMPARE_BY_COLUMN_KEY,
              label: compareColumnLabel,
              width: "",
            },
          ]
        : []),
      { key: "transcript", label: "Result", width: "150px" },
    ]

    const defaultMetricColumns: RunResultsTableColumn[] = Object.entries(
      metricRenderAdaptersById || {},
    )
      .filter(([_, metric]) => metric.metric_display_name != null)
      .filter(([metricKey]) => {
        if (metricIdFilter != null && metricIdFilter !== "") {
          return metricKey === metricIdFilter
        }
        return true
      })
      .filter(([metricKey]) => {
        return metricViews?.[metricKey]?.visible !== false
      })
      .filter(([_metricName, metric]) => {
        return runRows.some(
          (row) =>
            row.metric_outputs?.[metric.id] !== undefined &&
            row.metric_outputs[metric.id] !== null,
        )
      })
      .filter(
        ([_, metric]) =>
          metric.metric_display_name !== "Input" &&
          metric.metric_name?.toLowerCase() !== "input",
      )
      .map(([metricName, metric]) => ({
        key: metricName,
        kind: "metric" as const,
        label: getMetricDisplayLabel(metricName, isCompactColumns),
        fullLabel: resolveMetricDisplayName(
          metric.metric_display_name,
          metricName,
        ),
      }))
      .sort((a, b) => {
        const aIsAudioDuration =
          metricRenderAdaptersById[a.key]?.metric_manager ===
          "audio_duration_metric_manager"
        const bIsAudioDuration =
          metricRenderAdaptersById[b.key]?.metric_manager ===
          "audio_duration_metric_manager"
        if (aIsAudioDuration && !bIsAudioDuration) {
          return -1
        }
        if (!aIsAudioDuration && bIsAudioDuration) {
          return 1
        }
        return 0
      })
    const metricColumnsByKey = new Map(
      defaultMetricColumns.map((column) => [column.key, column]),
    )
    const metricColumns = orderMetricIds(
      defaultMetricColumns.map((column) => column.key),
      metricViews,
    ).flatMap((key) => metricColumnsByKey.get(key) ?? [])

    const hasAnyExplanation =
      explanationTable.columns.length > 0 &&
      runRows.some((row) =>
        Object.values(explanationTable.getRowValues(row)).some(
          (v) => v.trim() !== "",
        ),
      )

    const explanationColumnDefs: RunResultsTableColumn[] = hasAnyExplanation
      ? explanationTable.columns.map((col) => ({
          key: col.key,
          kind: "explanation" as const,
          label: col.title,
          fullLabel: col.title,
          ...(col.width != null && { width: col.width }),
          ...(col.minWidth != null && { minWidth: col.minWidth }),
          ...(col.maxWidth != null && { maxWidth: col.maxWidth }),
        }))
      : []

    return [...baseColumns, ...metricColumns, ...explanationColumnDefs]
  }, [
    metricRenderAdaptersById,
    runRows,
    isCompactColumns,
    getMetricDisplayLabel,
    metricIdFilter,
    showMutationColumn,
    compareColumnLabel,
    explanationTable,
    metricViews,
  ])
}
